import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { recruitmentCompanyService } from '../../services/recruitmentCompanyService';
import { authService } from '../../services/authService';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import Loader from '../../components/Loader';
import { Link, useNavigate } from 'react-router-dom';

const Users = () => {
    const user = authService.getCurrentUser();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [updatingId, setUpdatingId] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const navigate = useNavigate();
    const isEmployeeRole = (user?.user_recruitment_company_role || user?.recruitment_company_role || user?.roleInRecruitmentCompany) === 'employee';

    const loadUsers = async () => {
        try {
            setLoading(true);
            const res = await recruitmentCompanyService.getUsers();
            const list = Array.isArray(res) ? res : (res?.data ?? []);
            setUsers(Array.isArray(list) ? list : []);
        } catch (error) {
            const message = error?.response?.data?.message || 'فشل تحميل مستخدمي شركة التوظيف';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeRole = async (userId, newRole) => {
        try {
            setUpdatingId(userId);
            await recruitmentCompanyService.updateUser(userId, { role: newRole });
            toast.success('تم تحديث دور المستخدم');
            await loadUsers();
        } catch (error) {
            const message = error?.response?.data?.message || 'فشل تحديث الدور';
            toast.error(message);
        } finally {
            setUpdatingId(null);
        }
    };

    const handleRemove = async (userId) => {
        try {
            setRemovingId(userId);
            await recruitmentCompanyService.removeUser(userId);
            toast.success('تم إزالة المستخدم من شركة التوظيف');
            await loadUsers();
        } catch (error) {
            const message = error?.response?.data?.message || 'فشل إزالة المستخدم';
            toast.error(message);
        } finally {
            setRemovingId(null);
        }
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    return (
        <div className="employee-home-page" dir="rtl">
            <RecruitmentCompanyNav user={user} />

            {/* Hero + CTA */}
            <div className="px-3 mb-3 mt-3">
                <div className="edit-hero">
                    <div className="edit-hero-inner">
                        <div className="edit-hero-badge">👥</div>
                        <div className="edit-hero-text flex-grow-1">
                            <div className="edit-hero-title">مستخدمي شركة التوظيف</div>
                            <div className="edit-hero-subtitle">عرض وإدارة المستخدمين المرتبطين بشركة التوظيف</div>
                        </div>
                        {!isEmployeeRole && (
                            <Link
                                to="/recruitment-company/users/create"
                                className="btn-primary-modern"
                                style={{ textDecoration: 'none' }}
                            >
                                + إضافة مستخدم
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            {/* Users table */}
            <div className="all-jobs-section px-3 mb-5">
                {/* Toolbar */}
                <div className="d-flex gap-2 flex-wrap justify-content-between align-items-center mb-3">
                    <div className="searchbar-modern">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            type="text"
                            placeholder="ابحث بالاسم أو البريد..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="table-responsive" style={{ overflowX: 'auto' }}>
                    <table className="table users-table-modern">
                        <thead>
                            <tr>
                                <th>المستخدم</th>
                                <th>البريد الإلكتروني</th>
                                <th>الجوال</th>
                                <th>الدور</th>
                                <th>إجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users
                                .filter((u) => {
                                    const q = search.trim().toLowerCase();
                                    if (!q) return true;
                                    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                                })
                                .map((u, idx) => (
                                    <tr key={u.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="avatar-initial">
                                                    {(u.name || 'U').charAt(0).toUpperCase()}
                                                </div>
                                                <div className="user-meta">
                                                    <div className="user-name-strong">{u.name || '—'}</div>
                                                    <div className="user-id-light">#{u.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {u.email ? (
                                                <a href={`mailto:${u.email}`} className="link-soft">{u.email}</a>
                                            ) : '—'}
                                        </td>
                                        <td dir='ltr'>
                                            {u.phone ? (
                                                <a href={`tel:${u.phone}`} className="link-soft">{u.phone}</a>
                                            ) : '—'}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className={`pill ${(u.pivot?.role || 'employee') === 'admin' ? 'pill-admin' : 'pill-employee'}`}>
                                                    {(u.pivot?.role || 'employee') === 'admin' ? 'مشرف' : 'موظف'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <Link to={`/recruitment-company/users/${u.id}/edit`} className="btn-chip btn-chip-primary">
                                                    تعديل
                                                </Link>
                                                <button
                                                    onClick={() => handleRemove(u.id)}
                                                    className="btn-chip btn-chip-danger"
                                                    disabled={removingId === u.id}
                                                >
                                                    {removingId === u.id ? 'جارٍ الحذف...' : 'حذف'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ padding: '16px', textAlign: 'center' }}>
                                        لا يوجد مستخدمون لشركة التوظيف
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Local page styles (reuse edit-hero/edit-card/form-control-modern) */}
            <style>
                {`
          .users-table-modern {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            background: #fff;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 10px 30px rgba(0,0,0,0.05);
            border: 1px solid #eef2f7;
          }
          .users-table-modern thead tr {
            background: #f8fafc;
            position: sticky;
            top: 0;
            z-index: 1;
          }
          .users-table-modern th, .users-table-modern td {
            padding: 12px 14px;
            text-align: right;
            white-space: nowrap;
          }
          .users-table-modern tbody tr:nth-child(odd) {
            background: #fcfcfd;
          }
          .users-table-modern tbody tr:hover {
            background: #f6f9ff;
          }
          .user-cell {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .avatar-initial {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            display: grid;
            place-items: center;
            background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
            color: #fff;
            font-weight: 800;
            box-shadow: 0 6px 14px rgba(76, 116, 179, 0.25);
          }
          .user-meta {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }
          .user-name-strong {
            font-weight: 700;
            color: #0f172a;
          }
          .user-id-light {
            font-size: 0.8rem;
            color: #64748b;
          }
          .pill {
            padding: 4px 8px;
            border-radius: 999px;
            font-size: 0.8rem;
            border: 1px solid #e5e7eb;
          }
          .pill-admin {
            background: #f0f7ff;
            color: #1d4ed8;
            border-color: #bfdbfe;
          }
          .pill-employee {
            background: #f7f7f7;
            color: #111827;
          }
          .select-compact {
            min-width: 120px;
          }
          .table-actions {
            display: flex;
            gap: 8px;
          }
          .btn-chip {
            border: none;
            border-radius: 999px;
            padding: 8px 12px;
            font-weight: 600;
            cursor: pointer;
          }
          .btn-chip-primary {
            background: #eef2ff;
            color: #3730a3;
          }
          .btn-chip-danger {
            background: #fee2e2;
            color: #b91c1c;
          }
          .link-soft {
            color: #334155;
            text-decoration: none;
          }
          .link-soft:hover {
            color: #111827;
            text-decoration: underline;
          }
          .searchbar-modern {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #fff;
            box-shadow: 0 4px 12px rgba(0,0,0,0.04);
          }
          .searchbar-modern input {
            border: none;
            outline: none;
            background: transparent;
            min-width: 220px;
          }
          .edit-hero {
            background: linear-gradient(135deg, rgba(96,143,211,0.15) 0%, rgba(74,123,196,0.15) 100%);
            border-radius: 18px;
            padding: 14px 16px;
          }
          .edit-hero-inner {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .edit-hero-badge {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: grid;
            place-items: center;
            background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
            color: #fff;
            font-size: 22px;
            box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
          }
          .edit-hero-title {
            font-weight: 800;
            font-size: 1.1rem;
          }
          .edit-hero-subtitle {
            color: #475569;
            font-size: 0.9rem;
          }
          .edit-card {
            background: #ffffff;
            border-radius: 16px;
            padding: 18px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.05);
            border: 1px solid #eef2f7;
          }
          .grid-2 {
            display: grid;
            grid-template-columns: 1fr;
            gap: 14px;
          }
          @media (min-width: 768px) {
            .grid-2 {
              grid-template-columns: 1fr 1fr;
            }
          }
          .form-group-modern {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }
          .form-control-modern {
            width: 100%;
            border: 1px solid #e5e7eb;
            background: #fafafa;
            padding: 12px 14px;
            border-radius: 12px;
            outline: none;
            transition: box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
          }
          .form-control-modern:focus {
            border-color: #4a7bc4;
            background: #fff;
            box-shadow: 0 0 0 4px rgba(74, 123, 196, 0.15);
          }
        `}
            </style>

            <RecruitmentCompanyBottomNavigation />
        </div>
    );
};

export default Users;

